import React, { Fragment, useEffect } from 'react';
import { DashboardSkeleton } from 'pages/ReportsPage/components/AnalyticsDashboards/Skeleton';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { useLaborCostDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/store/laborCostDashboard.store';
import { useLaborCostKpiCards } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/useLaborCostKpiCards';
import { KpiStatCardRow } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCardRow';
import { FilterBar } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar';
import { WidgetCardWrapper } from 'pages/ReportsPage/components/AnalyticsDashboards/WidgetCardWrapper';
import { TypesOfHoursChart } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/TypesOfHoursChart';
import { CalloutTimeframeChart } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/CalloutTimeframeChart';
import { CalloutFulfillmentRateChart } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/CalloutFulfillmentRateChart';
import { ResolutionSpeedChart } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/ResolutionSpeedChart';
import { DailyOvertimeTable } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/DailyOvertimeTable';
import { WeeklyOvertimeTable } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/WeeklyOvertimeTable';
import {
  DashboardContainer,
  DashboardHeader,
  DashboardContent,
  DashboardTitle,
  DashboardErrorContainer,
  WidgetGrid,
  FullWidthSection,
} from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/LaborCostDashboard.styled';

export const LaborCostDashboard: React.FC = () => {
  const fetchData = useLaborCostDashboardStore((s) => s.fetchData);
  const reset = useLaborCostDashboardStore((s) => s.reset);
  const isLoading = useLaborCostDashboardStore((s) => s.isLoading);
  const error = useLaborCostDashboardStore((s) => s.error);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const kpiCards = useLaborCostKpiCards();
  const showSkeleton = isLoading;

  useEffect(() => {
    return fetchData(dateRange, locationIds, positions, employmentTypes);
  }, [fetchData, dateRange, locationIds, employmentTypes, positions]);

  useEffect(() => {
    return () => reset();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardContainer aria-label="Labor Cost Optimization Dashboard">
      <DashboardHeader>
        <DashboardTitle>Labor Cost Optimization</DashboardTitle>
      </DashboardHeader>
      <FilterBar />
      <DashboardContent>
        {error ? (
          <div aria-live="assertive">
            <DashboardErrorContainer>{error}</DashboardErrorContainer>
          </div>
        ) : showSkeleton ? (
          <DashboardSkeleton kpiCount={4} widgetCount={4} />
        ) : (
          <Fragment>
            <KpiStatCardRow cards={kpiCards} />
            <FullWidthSection>
              <WidgetCardWrapper title="Types of Hours" subtitle="">
                <TypesOfHoursChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <WidgetGrid>
              <WidgetCardWrapper
                title="Most common timeframe to start the call out shifts"
                subtitle="Number of call out shifts by day and timeframe"
              >
                <CalloutTimeframeChart />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title="Fulfillment Rate of call out shifts"
                subtitle="Fulfillment rate by time of day"
              >
                <CalloutFulfillmentRateChart />
              </WidgetCardWrapper>
            </WidgetGrid>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Average speed to resolve call outs"
                subtitle="How long did it take from call out creation to shift acceptance, in hours"
              >
                <ResolutionSpeedChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Daily Overtime Details"
                subtitle="Team members who exceeded the daily working hour limit"
              >
                <DailyOvertimeTable />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Weekly Overtime Details"
                subtitle="Team members who exceeded the weekly working hour limit"
              >
                <WeeklyOvertimeTable />
              </WidgetCardWrapper>
            </FullWidthSection>
          </Fragment>
        )}
      </DashboardContent>
    </DashboardContainer>
  );
};
